import * as _ from 'lodash';
import { Component, OnInit } from '@angular/core';
import { RouteService } from 'src/app/core/services';
import { CoursesService } from 'src/app/services/courses.service';
import { SurveyRadioTableAnswer, SurveyQuestion, SurveyTextareaAnswer, Survey } from 'src/app/models/survey';
import { FormatHelper } from 'src/app/helpers';

type UIComment = {
   show: boolean;
   text: string;
};

@Component({
  selector: 'app-post-evaluation-rw',
  templateUrl: './post-evaluation-rw.component.html',
  styleUrls: ['./post-evaluation-rw.component.css']
})
export class PostEvaluationRwComponent implements OnInit {
  courseComplete = false;

  question1: SurveyQuestion<SurveyRadioTableAnswer> = {
    question: 'As a result of RentWise, what do you now know about these topics? Please answer each question by selecting one of the numbers for <u>EACH</u> question.',
    answer: {
      type: 'radio_table',
      questions: [
        'How to manage my money well',
        'How to handle credit cards or loans',
        'How to organize and file records and bills',
        'How to reduce energy use and energy bills',
        'How to find a safe adequate place to live',
        'How to communicate well with landlords',
        'How to keep my home clean',
        'How to be a good renter',
        'Ways to reduce spending',
        'Ways to improve my credit',
        'About the Nebraska rental regulations',
        'What my tenant rights and responsibilities are',
        'What the landlords’ rights and responsibilities are',
        'What is in my lease or rental contract',
        'What landlords check on my credit reports',
        'What to do when moving in',
        'What to do when moving out',
        'What renters’ insurance covers',
        'Where to go for help if I feel someone has been discriminated against in renting',
      ],
      options: ['1', '2', '3', '4'],
      answers: [] as string[],
      comments: [] as UIComment[] as any[]
    }
  };

  question2: SurveyQuestion<SurveyRadioTableAnswer> = {
    question: 'Please tell us about your experiences as a renter and whether you agree or disagree with the following statements. Answer each question by circling one number for <u>EACH</u> question.',
    answer: {
      type: 'radio_table',
      questions: [
        'I am good at managing or handling my money',
        'I have a spending plan',
        'I pay my rent on time',
        'I am good at talking and communicating with others',
        'I keep my home clean',
        'I know how to keep mold and moisture out of my unit',
        'My landlord thinks I am a good renter',
        'I think I am a good renter',
        'If I were a landlord, I would rent to someone like me',
        'I usually get my rental deposit back',
        'I have a good rental history',
        'I organize my information before completing a rental application',
        'I leave the unit clean when I move out',
        'I complete a move-in checklist when I move in',
        'I try to conserve energy in my home or unit',
      ],
      options: ['1', '2', '3', '4'],
      answers: [] as string[],
      comments: [] as UIComment[] as any[]
    }
  };

  question3: SurveyQuestion<SurveyTextareaAnswer> = {
    question: 'As a renter, I now do these things well – please list. What has changed or improved? (Example: Pay my bills on time.)',
    answer: {
      type:  'textarea',
      text: ''
    }
  };

  question4: SurveyQuestion<SurveyTextareaAnswer> = {
    question: 'What more do you need to know to become a better renter? Please list here.',
    answer: {
      type:  'textarea',
      text: ''
    }
  };

  question5: SurveyQuestion<SurveyTextareaAnswer> = {
    question: 'What have you learned in RentWise? What was important or new to you?',
    answer: {
      type:  'textarea',
      text: ''
    }
  };

  question6: SurveyQuestion<SurveyTextareaAnswer> = {
    question: 'Has RentWise made a difference for you? If yes, how? Please comment.',
    answer: {
      type:  'textarea',
      text: ''
    }
  };

  submitting = false;

  constructor(private courseService: CoursesService, private routeService: RouteService) {}

  ngOnInit(): void {
    this.initQuestions();
  }

  get questions() {
    return [this.question1, this.question2, this.question3, this.question4, this.question5, this.question6];
  }

  ui = {
    comment: (v: unknown) => v as UIComment
  };

  private initQuestions() {
    const postTest = this.courseService.currentCourse.postTest as Survey;

    if (postTest) {
      this.question1 = (postTest.questions[0] as SurveyQuestion<SurveyRadioTableAnswer>) || this.question1;
      this.question2 = (postTest.questions[1] as SurveyQuestion<SurveyRadioTableAnswer>) || this.question2;
      this.question3 = (postTest.questions[2] as SurveyQuestion<SurveyTextareaAnswer>) || this.question3;
      this.question4 = (postTest.questions[3] as SurveyQuestion<SurveyTextareaAnswer>) || this.question4;
      this.question5 = (postTest.questions[4] as SurveyQuestion<SurveyTextareaAnswer>) || this.question5;
      this.question6 = (postTest.questions[5] as SurveyQuestion<SurveyTextareaAnswer>) || this.question6;
    }

    this.questions.forEach((q, i) => {
      q.questionNumber = q.questionNumber || (i + 1);

      if (q.answer.type === 'radio_table') {
        q.answer.answers  = q.answer.questions.map(q => '');
        q.answer.comments = q.answer.questions.map(q => ({ show: false, text: '' }) as UIComment) as any[];
      }
    });
  }

  letter(index: number) {
    return FormatHelper.letter(index);
  }

  table_radioGroup_keypress(answer: SurveyRadioTableAnswer, option: number, event: KeyboardEvent) {
    if (answer.options.includes(event.key)) {
      answer.answers[option] = event.key;
    }
  }

  table_radio_click(answer: SurveyRadioTableAnswer, value: string, option: number, event: MouseEvent) {
    answer.answers[option] = value;

    // select this option for all answers / radio groups when you click 3 times in a row (1st row only) - trick for debugging
    if (option === 0 && event.detail === 3) {
      this.setAll(answer, value);
    }

    // select this option for all answers / radio groups across all the questions when you click 5 times in a row (1st row only) - trick for debugging
    if (option === 0 && event.detail === 5) {
      this.questions.forEach(q => {
        if (q.answer.type === 'radio_table') {
          this.setAll(q.answer, value);
        }
      });
    }
  }

  private setAll(answer: SurveyRadioTableAnswer, value: string) {
    answer.questions.forEach((q, i) => {
      answer.answers[i] = value;
    });
  }

  table_toggleComment(question: SurveyQuestion<SurveyRadioTableAnswer>, option: number) {
    const comment = this.ui.comment(question.answer.comments[option]);
    comment.show = !comment.show;
  }

  async submitForm() {
    if (this.submitting) {
      return;
    }

    this.submitting = true;

    try {
      const course = this.courseService.currentCourse;
      const data   = this.prepare(this.questions);

      await this.courseService.sendPostEvaluationForm(course, data).toPromise();

      // Reload courses from the database for this User
      await this.courseService.getUserCourses({ getmodules: true }).toPromise().then((res: any) => {
        this.courseService.courses = res.courses || [];
        this.courseService.reloadCurrentCourse();
      });

      this.courseComplete = true;

      // Scroll to top
      this.scrollToTop();
    } catch (err) {
      this.submitting = false;
      throw err;
    }
  }

  private prepare(questions: SurveyQuestion[]) {
    return questions.map((q, i) => {
      let question = { ...q };

      if (question.answer.type === 'radio_table') {
        const uiComments = question.answer.comments.map(this.ui.comment);

        question.answer.comments = uiComments.map(c => c.show ? c.text : '');
      }

      return question;
    });
  }

  private scrollToTop() {
    setTimeout(() => this.routeService.scrollToTop());
  }
}
